import { FC, ReactNode } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Typography
} from "@mui/material";
import { Country } from "../../../../api/Api";
import { Colors } from "../../../../theme";
import { getStatus } from "../../../../components/lifecycle/departmentProgress/utils";
import { getSelectedParties } from "../mftTasks/parties/utils";

type Props = {
  country: Country;
};

const fmt = (value: unknown): ReactNode => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const Field: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      gap: { xs: 0.25, sm: 2 },
      paddingY: 0.5
    }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ flex: { sm: "0 0 180px" } }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ flex: 1 }}>
      {value}
    </Typography>
  </Box>
);

const Section: FC<{
  title: string;
  headerAction?: ReactNode;
  showDivider?: boolean;
  children: ReactNode;
}> = ({ title, headerAction, showDivider = true, children }) => (
  <Card
    variant="outlined"
    elevation={3}
    sx={{ breakInside: "avoid", marginBottom: 2 }}
  >
    <CardContent sx={{ padding: 2, "&:last-child": { paddingBottom: 2 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          marginBottom: showDivider ? 1 : 0
        }}
      >
        <Typography variant="h6">{title}</Typography>
        {headerAction}
      </Box>
      {showDivider && <Divider sx={{ marginBottom: 1 }} />}
      {children}
    </CardContent>
  </Card>
);

const SignOffNote: FC<{ note?: string | null }> = ({ note }) =>
  note ? <Field label="Sign-off note" value={note} /> : null;

const LifecycleStatus: FC<{
  progress?: string | null;
  hasSignOffNote?: boolean;
}> = ({ progress, hasSignOffNote = false }) => {
  const completed = getStatus(progress) === "completed";
  const isGreen = completed && !hasSignOffNote;
  const label = completed
    ? hasSignOffNote
      ? "Completed with remarks"
      : "Completed"
    : "Not completed";

  return (
    <Chip
      size="small"
      label={label}
      sx={{
        backgroundColor: isGreen ? Colors.NICE_GREEN : Colors.MFT_ORANGE,
        color: "#FFF",
        flexShrink: 0,
        marginTop: "2px"
      }}
    />
  );
};

/**
 * Read-only overview for the VPs signing off. Per ADHO-443 each lifecycle is a status chip plus its
 * sign-off note and nothing else, to keep the reader on what is un-done. The field-by-field detail,
 * including every party row, lives in the PDF.
 *
 * The one country-specific addition is the party list under Basic Information: which TSO, exchange,
 * bank and so on this setup depends on is the single most useful thing a VP can see at a glance, and
 * it is what every lifecycle below is working through.
 */
export const SignOffDetailsPanel: FC<Props> = ({ country }) => {
  const marketAccess = country.marketAccessLifecycle;
  const compliance = country.complianceLifecycle;
  const legal = country.legalLifecycle;
  const risk = country.riskLifecycle;
  const creditRisk = country.creditRiskLifecycle;
  const treasury = country.treasuryLifecycle;
  const finance = country.financeLifecycle;
  const tech = country.techLifecycle;

  const parties = getSelectedParties(marketAccess);

  const lifecycles = [
    {
      title: "Compliance",
      progress: country.progress?.compliance,
      note: compliance?.signOffNote
    },
    {
      title: "Legal",
      progress: country.progress?.legal,
      note: legal?.signOffNote
    },
    {
      title: "Risk",
      progress: country.progress?.risk,
      note: risk?.signOffNote
    },
    {
      title: "Credit Risk",
      progress: country.progress?.creditRisk,
      note: creditRisk?.signOffNote
    },
    {
      title: "Treasury",
      progress: country.progress?.treasury,
      note: treasury?.signOffNote
    },
    {
      title: "Finance",
      progress: country.progress?.finance,
      note: finance?.signOffNote
    },
    {
      title: "Technology",
      progress: country.progress?.tech,
      note: tech?.signOffNote
    }
  ];

  return (
    <Box padding={1}>
      <Card elevation={3}>
        <CardContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ marginBottom: 1 }}>
                Basic Information
              </Typography>
              <Divider sx={{ marginBottom: 1 }} />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                flex: 1,
                gap: 3
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Field label="Country" value={fmt(country.countryName)} />
                <Field label="Trading team" value={fmt(country.tradingTeam)} />
                <Field
                  label="Contracting entity"
                  value={fmt(country.contractingEntity)}
                />
                <Field label="Product type" value={fmt(country.productType)} />
                <Field label="Venue" value={fmt(country.venue)} />
                <Field
                  label="Settlement type"
                  value={fmt(country.tradingSettlementType)}
                />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Field
                  label="Transaction type"
                  value={fmt(country.transactionType)}
                />
                <Field label="Mandate granted" value={fmt(country.mandate)} />
                <Field label="Deal unit" value={fmt(country.unit)} />
                <Field
                  label="Expected deal volume/size"
                  value={
                    country.dealVolume
                      ? `${country.dealVolume} ${country.unit || ""}`.trim()
                      : "N/A"
                  }
                />
                <Field
                  label="Parties"
                  value={
                    parties.length === 0
                      ? "None marked needed yet"
                      : parties
                          .map((party) =>
                            party.name
                              ? `${party.name} (${party.type.replace(/_/g, " ")})`
                              : party.type.replace(/_/g, " ")
                          )
                          .join(", ")
                  }
                />
              </Box>
            </Box>
            {country.businessRational && (
              <Field
                label="Business rational"
                value={country.businessRational}
              />
            )}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <SignOffNote note={marketAccess?.signOffNote} />
            </Box>
          </Box>

          <Box
            sx={{ marginTop: 2, columnCount: { xs: 1, lg: 2 }, columnGap: 4 }}
          >
            {lifecycles.map(({ title, progress, note }) => (
              <Section
                key={title}
                title={title}
                headerAction={
                  <LifecycleStatus
                    progress={progress}
                    hasSignOffNote={Boolean(note)}
                  />
                }
                showDivider={Boolean(note)}
              >
                <SignOffNote note={note} />
              </Section>
            ))}
          </Box>

          <Box sx={{ marginTop: 2 }}>
            {country.signOff?.summary && (
              <Section title="Summary">
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {country.signOff.summary}
                </Typography>
              </Section>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
